<template>
  <section
    class="verified-anomalies-table"
    :data-density="density"
    :style="{ '--row-height': `var(--table-row-${density})` }"
    aria-label="AVA Inspect verified anomalies"
    :aria-busy="String(loading)">
    <header class="verified-heading">
      <div class="verified-heading__start">
        <slot name="header-start"></slot>
      </div>
      <div class="verified-heading__actions">
        <slot name="header-actions"></slot>
      </div>
    </header>
    <slot name="filter-context"></slot>
    <div v-if="pageSelectedIds.length" class="bulk-bar">
      <strong>{{ pageSelectedIds.length }} selected on this page</strong
      ><slot name="bulk-actions"></slot
      ><v-btn small text :disabled="busy" @click="$emit('update:selected-ids', [])"
        >Clear selection</v-btn
      >
    </div>
    <div v-if="error" class="error-banner" role="alert">
      <span
        >{{
          groups.length
            ? 'Refresh failed. Showing previously loaded anomalies. '
            : 'Could not load verified anomalies. '
        }}{{ error }}</span
      ><v-btn small text :disabled="loading" @click="$emit('retry')">Retry</v-btn>
    </div>
    <div v-if="loading && !groups.length" class="state-block" role="status">
      <v-progress-circular indeterminate color="primary" :size="28" />
      <p>Loading verified anomalies…</p>
    </div>
    <div v-else-if="!groups.length && !error" class="state-block">
      <v-icon :size="30">mdi-check-decagram-outline</v-icon>
      <h3>{{ filtersApplied ? 'No matching verified anomalies' : 'No verified anomalies yet' }}</h3>
      <p>
        {{
          filtersApplied
            ? 'Try adjusting your search or filters.'
            : 'Accepted findings of AVA Inspect runs in AI Analysis will appear here, under their AVA Inspect tag.'
        }}
      </p>
      <v-btn v-if="filtersApplied" small outlined color="primary" @click="$emit('clear-filters')"
        >Clear filters</v-btn
      ><v-btn v-else small outlined color="primary" @click="$emit('open-analysis')"
        >Open AI Analysis</v-btn
      >
    </div>
    <div
      v-if="groups.length"
      class="verified-body"
      :class="{ 'verified-body--with-panel': !!$slots.panel }">
      <div class="table-wrap">
        <v-progress-linear v-if="loading" indeterminate height="2" class="table-refresh" />
        <table class="eg">
          <colgroup>
            <col class="c-select" />
            <col class="c-id" />
            <col class="c-component" />
            <col class="c-location" />
            <col class="c-defect" />
            <col class="c-priority" />
            <col class="c-plans" />
            <col class="c-actions" />
          </colgroup>
          <thead>
            <tr>
              <th class="col-select">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate.prop="someSelected"
                  :disabled="busy || loading"
                  aria-label="Select all anomalies on this page"
                  @change="selectPage($event.target.checked)" />
              </th>
              <th class="col-id" :aria-sort="sortState('FriendlyId')">
                <button
                  type="button"
                  class="sort-button"
                  :disabled="loading"
                  @click="sort('FriendlyId')">
                  Anomaly No.<v-icon v-if="isSorted('FriendlyId')" :size="13">{{
                    descending ? 'mdi-arrow-down' : 'mdi-arrow-up'
                  }}</v-icon>
                </button>
              </th>
              <th>Component</th>
              <th>Scan / Location</th>
              <th>Defect</th>
              <th :aria-sort="sortState('Severity')">
                <button
                  type="button"
                  class="sort-button"
                  :disabled="loading"
                  @click="sort('Severity')">
                  Work order priority<v-icon v-if="isSorted('Severity')" :size="13">{{
                    descending ? 'mdi-arrow-down' : 'mdi-arrow-up'
                  }}</v-icon>
                </button>
              </th>
              <th>Plans</th>
              <th class="actions col-actions"><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <!-- One tbody per AVA Inspect tag: the tag, its master tag, then its findings. -->
          <tbody
            v-for="group in groups"
            :key="group.avaInspectId"
            class="ava-group"
            :class="{ 'is-open': isOpen(group) }">
            <tr class="ava-group__tag">
              <td :colspan="columnCount">
                <div class="ava-group__line">
                  <button
                    type="button"
                    class="ava-group__toggle"
                    :aria-expanded="String(isOpen(group))"
                    :aria-label="`${isOpen(group) ? 'Collapse' : 'Expand'} AVA Inspect ${group.avaInspectName}`"
                    @click="$emit('toggle-group', group.avaInspectId)">
                    <v-icon :size="18" class="ava-group__chevron">{{ isOpen(group) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                    <v-icon :size="16" class="ava-group__icon">mdi-vector-square</v-icon>
                    <span class="ava-group__name mono-id">{{ group.avaInspectName || group.avaInspectId }}</span>
                    <span v-if="group.avaInspectTitle" class="ava-group__title">{{ group.avaInspectTitle }}</span>
                    <span class="ava-group__count">{{ group.findingCount }} {{ group.findingCount === 1 ? 'finding' : 'findings' }}</span>
                  </button>
                  <span v-if="group.assessment" class="ava-group__grade" :title="gradeTitle(group.assessment)">
                    <span class="ava-group__grade-label">Element grade</span>
                    <span :class="['defect-chip', `defect-chip--ri-${riLevel(group.assessment.ri)}`]">{{ group.assessment.ri || 'Ri —' }}</span>
                    <span :class="['priority-pill', `priority-pill--${riskLevel(group.assessment.riskCategory)}`]">RBI {{ group.assessment.riskCategory }} · {{ group.assessment.riskMatrixCell }}</span>
                  </span>
                </div>
              </td>
            </tr>
            <template v-if="isOpen(group)">
              <tr class="ava-group__master">
                <td :colspan="columnCount">
                  <div class="ava-group__line ava-group__line--master">
                    <v-icon :size="14" class="ava-group__master-icon">mdi-tag-outline</v-icon>
                    <span class="ava-group__master-label">Master tag</span>
                    <template v-if="group.masterTag">
                      <strong class="ava-group__master-name">{{ group.masterTag.name }}</strong>
                      <span v-if="tagTypeLabel(group.masterTag.type)" class="ava-group__master-type">{{ tagTypeLabel(group.masterTag.type) }}</span>
                    </template>
                    <span v-else class="ava-group__master-none">No master tag</span>
                  </div>
                </td>
              </tr>
              <tr v-if="statusOf(group).loading && !entriesOf(group).length" class="ava-group__state">
                <td :colspan="columnCount"><div class="ava-group__line ava-group__line--findings">Loading findings…</div></td>
              </tr>
              <tr v-else-if="statusOf(group).error" class="ava-group__state ava-group__state--error">
                <td :colspan="columnCount">
                  <div class="ava-group__line ava-group__line--findings" role="alert">
                    {{ statusOf(group).error }}
                    <v-btn small text :disabled="busy" @click="$emit('retry-group', group.avaInspectId)">Retry</v-btn>
                  </div>
                </td>
              </tr>
              <verified-anomaly-row
                v-for="entry in entriesOf(group)"
                :key="entry.id"
                class="ava-group__finding"
                :finding="entry"
                :scan-name="scanName(entry)"
                :active="entry.id === activeId"
                :selected="selectedSet.has(entry.id)"
                :busy="busy || loading"
                @select="selectOne"
                @open="$emit('open', $event)"
                @report="$emit('report', $event)"
                @link="$emit('link', $event)"
                @unlink="$emit('unlink', $event)"
                @isolate="$emit('isolate', $event)"
                @corrosion="$emit('corrosion', $event)"
                @delete="$emit('delete', $event)" />
            </template>
          </tbody>
        </table>
      </div>
      <slot name="panel"></slot>
    </div>
    <footer v-if="total > 0" class="pagination">
      <div class="pagination__settings" aria-label="Table settings">
        <label
          >Rows per page
          <select
            :value="pageSize"
            :disabled="loading || busy"
            @change="setPageSize($event.target.value)">
            <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
          </select>
        </label>
      </div>
      <span aria-live="polite">{{ rangeLabel }}</span
      ><v-btn
        icon
        small
        :disabled="loading || busy || page <= 1"
        aria-label="Previous page"
        @click="setPage(page - 1)"
        ><v-icon>mdi-chevron-left</v-icon></v-btn
      ><v-btn
        icon
        small
        :disabled="loading || busy || page >= pageCount"
        aria-label="Next page"
        @click="setPage(page + 1)"
        ><v-icon>mdi-chevron-right</v-icon></v-btn
      >
      <div class="pagination__density">
          <density-toggle :value="density" @input="$emit('update:density', $event)" />
      </div>
    </footer>
  </section>
</template>

<script>
  import DensityToggle from '../aiAnalysis/DensityToggle.vue';
  import { DENSITIES, isDensity } from '../aiAnalysis/densityPreference';
  import VerifiedAnomalyRow from './VerifiedAnomalyRow.vue';
  /** Columns of the table (see colgroup); group rows span all of them. */
  const COLUMN_COUNT = 8;
  /** RBI risk category -> priority-pill level, lowest to highest. */
  const RISK_LEVELS = Object.freeze({ low: 'level-0', medium: 'level-1', 'medium-high': 'level-2', high: 'level-3' });
  export default {
    name: 'VerifiedAnomaliesTable',
    components: { DensityToggle, VerifiedAnomalyRow },
    props: {
      /** Findings of the loaded groups; each carries its avaInspectId. */
      entries: { type: Array, default: () => [] },
      /** The page of AVA Inspect groups (tag, its master tag, finding count, element grade). */
      groups: { type: Array, default: () => [] },
      expandedGroupIds: { type: Array, default: () => [] },
      /** avaInspectId -> { loading, error } of a group's findings. */
      groupStatus: { type: Object, default: () => ({}) },
      images: { type: Array, default: () => [] },
      total: { type: Number, default: 0 },
      loading: { type: Boolean, default: false },
      error: { type: String, default: null },
      options: {
        type: Object,
        default: () => ({ page: 1, itemsPerPage: 10, sortBy: ['CreateDate'], sortDesc: [true] }),
      },
      selectedIds: { type: Array, default: () => [] },
      activeId: { type: String, default: null },
      density: { type: String, default: DENSITIES.DEFAULT, validator: isDensity },
      filtersApplied: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
    },
    computed: {
      selectedSet() {
        return new Set(this.selectedIds);
      },
      pageSelectedIds() {
        return this.entries
          .filter((entry) => this.selectedSet.has(entry.id))
          .map((entry) => entry.id);
      },
      allSelected() {
        return this.entries.length > 0 && this.pageSelectedIds.length === this.entries.length;
      },
      someSelected() {
        return this.pageSelectedIds.length > 0 && !this.allSelected;
      },
      imagesById() {
        return new Map(this.images.map((image) => [image.id, image]));
      },
      columnCount() {
        return COLUMN_COUNT;
      },
      expandedSet() {
        return new Set(this.expandedGroupIds);
      },
      entriesByGroup() {
        return this.entries.reduce((acc, entry) => {
          (acc[entry.avaInspectId] = acc[entry.avaInspectId] || []).push(entry);
          return acc;
        }, {});
      },
      page() {
        return Math.max(1, Number(this.options.page) || 1);
      },
      pageSize() {
        return Math.max(1, Number(this.options.itemsPerPage) || 10);
      },
      pageSizes() {
        return [...new Set([10, 25, 50, 100, this.pageSize])].sort((a, b) => a - b);
      },
      pageCount() {
        return Math.max(1, Math.ceil(this.total / this.pageSize));
      },
      descending() {
        return Boolean(
          Array.isArray(this.options.sortDesc) ? this.options.sortDesc[0] : this.options.sortDesc,
        );
      },
      rangeLabel() {
        const start = this.groups.length ? (this.page - 1) * this.pageSize + 1 : 0;
        return `${start}–${Math.min(
          (this.page - 1) * this.pageSize + this.groups.length,
          this.total,
        )} of ${this.total} AVA Inspect ${this.total === 1 ? 'tag' : 'tags'}`;
      },
    },
    methods: {
      isOpen(group) {
        return this.expandedSet.has(group.avaInspectId);
      },
      entriesOf(group) {
        return this.entriesByGroup[group.avaInspectId] || [];
      },
      statusOf(group) {
        return this.groupStatus[group.avaInspectId] || {};
      },
      riLevel(ri) {
        const match = /^Ri\s*([0-5])$/i.exec(String(ri || '').trim());
        return match ? match[1] : 'custom';
      },
      riskLevel(category) {
        return RISK_LEVELS[String(category || '').toLowerCase()] || 'none';
      },
      /** "ForgeEquipment" -> "Equipment". */
      tagTypeLabel(type) {
        return String(type || '').replace(/^Forge/, '');
      },
      gradeTitle(assessment) {
        const extent = assessment.worstViewPercent != null ? `${Number(assessment.worstViewPercent).toFixed(1)}% corroded on the worst view · ` : '';
        return `${extent}PoF ${assessment.pof} × CoF ${assessment.cof} · ${assessment.snapCount} snaps in ${assessment.roundCount} rounds`;
      },
      scanName(entry) {
        return (this.imagesById.get(entry.imageId) || {}).name || entry.imageName || '';
      },
      selectPage(checked) {
        this.$emit('update:selected-ids', checked ? this.entries.map((entry) => entry.id) : []);
      },
      selectOne({ id, checked }) {
        const next = new Set(this.pageSelectedIds);
        if (checked) next.add(id);
        else next.delete(id);
        this.$emit('update:selected-ids', [...next]);
      },
      isSorted(key) {
        const sortBy = Array.isArray(this.options.sortBy)
          ? this.options.sortBy[0]
          : this.options.sortBy;
        return String(sortBy || '').toLowerCase() === key.toLowerCase();
      },
      sortState(key) {
        return this.isSorted(key) ? (this.descending ? 'descending' : 'ascending') : 'none';
      },
      sort(key) {
        this.$emit('update:options', {
          ...this.options,
          page: 1,
          sortBy: [key],
          sortDesc: [this.isSorted(key) ? !this.descending : false],
        });
      },
      setPage(page) {
        this.$emit('update:options', { ...this.options, page });
      },
      setPageSize(value) {
        this.$emit('update:options', { ...this.options, page: 1, itemsPerPage: Number(value) });
      },
    },
  };
</script>

<style lang="scss">
  @import '../aiAnalysis/aiAnalysisTokens';
  @import '../aiAnalysis/engineeringGrid';
  .verified-anomalies-table {
    @include ai-analysis-tokens;
    @include engineering-grid;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    min-height: 0;
    border: 1px solid var(--aa-border-default);
    border-radius: var(--ds-radius-md);
    background: var(--aa-surface);
    color: var(--aa-text-primary);
    .verified-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: var(--ds-space-md);
      padding: var(--ds-space-md);
    }
    .verified-heading__start {
      flex: 1 1 260px;
      min-width: 180px;
    }
    .verified-heading__actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: flex-end;
      gap: var(--ds-space-sm);
      min-width: 0;
    }
    .bulk-bar,
    .error-banner,
    .pagination {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--ds-space-sm);
      padding: var(--ds-space-sm) var(--ds-space-md);
      font-size: var(--ds-font-body-sm);
      border-top: 1px solid var(--aa-border-subtle);
    }
    .bulk-bar {
      background: var(--aa-brand-soft);
    }
    .error-banner {
      color: var(--aa-error);
    }
    .state-block {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--ds-space-xl);
      color: var(--aa-text-secondary);
    }
    .state-block h3 {
      margin: var(--ds-space-md) 0 var(--ds-space-sm);
      color: var(--aa-text-primary);
    }
    .verified-body {
      display: flex;
      flex: 1 1 auto;
      align-items: stretch;
      min-width: 0;
      min-height: 0;
    }
    .verified-body > .table-wrap {
      flex: 1 1 0;
      min-width: 0;
      min-height: 0;
      max-height: none;
      border-radius: 0;
    }
    table.eg {
      min-width: 1210px;
    }
    col.c-select {
      width: 40px;
    }
    col.c-id {
      width: 184px;
    }
    col.c-component {
      width: 195px;
    }
    col.c-location {
      width: 230px;
    }
    col.c-defect {
      width: 200px;
    }
    col.c-priority {
      width: 170px;
    }
    col.c-plans {
      width: 145px;
    }
    col.c-actions {
      width: 46px;
    }
    table.eg td,
    table.eg th {
      padding-right: 10px;
      padding-left: 10px;
    }
    table.eg .col-select {
      position: sticky;
      left: 0;
      z-index: var(--z-table-sticky-col);
      text-align: center;
      text-overflow: clip;
      padding: 0;
    }
    table.eg .col-id {
      position: sticky;
      left: 40px;
      z-index: var(--z-table-sticky-col);
      box-shadow: 2px 0 0 var(--aa-border-default);
    }
    table.eg .col-actions {
      position: sticky;
      right: 0;
      z-index: var(--z-table-sticky-col);
      padding: 0 6px;
      box-shadow: -2px 0 0 var(--aa-border-default);
    }
    table.eg th.col-select,
    table.eg th.col-id,
    table.eg th.col-actions {
      z-index: var(--z-table-sticky-corner);
    }
    table.eg tbody tr:hover td {
      background: var(--aa-surface);
      box-shadow: inset 0 0 0 9999px var(--aa-surface-hover);
    }
    table.eg tbody tr.is-selected td,
    table.eg tbody tr.is-active td {
      background: var(--aa-surface);
      box-shadow: inset 0 0 0 9999px var(--aa-surface-active);
    }
    // AVA Inspect groups: the tag row, its master tag row, then the findings, indented.
    tbody.ava-group + tbody.ava-group tr.ava-group__tag td {
      border-top: 2px solid var(--aa-border-default);
    }
    tr.ava-group__tag td,
    tr.ava-group__master td,
    tr.ava-group__state td {
      padding: 0;
    }
    table.eg tbody tr.ava-group__tag:hover td,
    table.eg tbody tr.ava-group__master:hover td,
    table.eg tbody tr.ava-group__state:hover td {
      box-shadow: none;
    }
    tr.ava-group__tag td {
      background: var(--aa-surface-subtle);
    }
    // Sticky to the left edge so the tag stays readable while the table scrolls sideways.
    .ava-group__line {
      position: sticky;
      left: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--ds-space-sm);
      width: max-content;
      max-width: 100%;
      min-height: var(--row-height);
      padding: 4px var(--ds-space-md) 4px 6px;
    }
    .ava-group__toggle {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-height: 32px;
      padding: 0 6px;
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-primary);
      font: inherit;
      cursor: pointer;
    }
    .ava-group__toggle:hover {
      background: var(--aa-surface-hover);
    }
    .ava-group__toggle:focus-visible {
      outline: 2px solid var(--aa-focus);
      outline-offset: 1px;
    }
    .ava-group__chevron,
    .ava-group__icon,
    .ava-group__master-icon {
      color: var(--aa-text-secondary) !important;
    }
    .ava-group__name {
      font-weight: 600;
    }
    .ava-group__title {
      color: var(--aa-text-secondary);
    }
    .ava-group__count {
      padding: 1px 8px;
      border-radius: 999px;
      background: var(--aa-brand-soft);
      color: var(--aa-text-secondary);
      font-size: var(--aa-grid-font-secondary);
      font-weight: 600;
    }
    .ava-group__grade {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .ava-group__grade-label,
    .ava-group__master-label {
      color: var(--aa-text-muted);
      font-size: var(--aa-grid-font-secondary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .ava-group__line--master {
      padding-left: 40px;
      gap: 6px;
    }
    .ava-group__master-type {
      padding: 1px 6px;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-secondary);
      font-size: var(--aa-grid-font-secondary);
    }
    .ava-group__master-none {
      color: var(--aa-text-muted);
    }
    .ava-group__line--findings {
      padding-left: 64px;
      color: var(--aa-text-secondary);
    }
    .ava-group__state--error .ava-group__line {
      color: var(--aa-error);
    }
    // Findings sit one level under the master tag.
    tr.ava-group__finding td.col-id {
      padding-left: 28px;
    }
    .sort-button {
      display: flex;
      align-items: center;
      gap: 4px;
      color: inherit;
      font: inherit;
      text-transform: inherit;
      letter-spacing: inherit;
      cursor: pointer;
    }
    .sort-button:focus-visible,
    .table-wrap input[type='checkbox']:focus-visible,
    .pagination select:focus-visible {
      outline: 2px solid var(--aa-focus);
      outline-offset: 2px;
    }
    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      accent-color: var(--aa-brand);
      vertical-align: middle;
      cursor: pointer;
    }
    .identity-cell {
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
    }
    .identity-cell .mono-id {
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--aa-text-primary);
    }
    .finding-position-status {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }
    .finding-position-status:focus-visible {
      outline: 2px solid var(--aa-focus);
      outline-offset: 2px;
    }
    .verified-component-wrap {
      display: flex;
      align-items: center;
      gap: 2px;
      min-width: 0;
    }
    .verified-component {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      max-width: 100%;
      height: 24px;
      padding: 0 6px;
      border: 1px solid transparent;
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-primary);
      font: inherit;
      cursor: pointer;
    }
    .verified-component:hover {
      border-color: var(--aa-border-default);
      background: var(--aa-surface-subtle);
    }
    .verified-component:focus-visible,
    .verified-component-unlink:focus-visible {
      outline: 2px solid var(--aa-focus);
      outline-offset: 1px;
    }
    .verified-component__icon,
    .verified-component__caret {
      flex: 0 0 auto;
      color: var(--aa-text-secondary) !important;
    }
    .verified-component__name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .verified-component--none {
      color: var(--aa-text-muted);
    }
    .verified-component-unlink {
      display: inline-grid;
      flex: 0 0 auto;
      place-items: center;
      width: 20px;
      height: 20px;
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-muted);
      opacity: 0;
    }
    tr:hover .verified-component-unlink,
    tr.is-active .verified-component-unlink,
    .verified-component-unlink:focus-visible {
      opacity: 1;
    }
    .verified-component-unlink:hover {
      color: var(--aa-error);
      background: rgba(183, 28, 28, 0.1);
    }
    @media (hover: none) {
      .verified-component-unlink {
        opacity: 1;
      }
    }
    .anomaly-thumb {
      width: 36px;
      height: 28px;
      object-fit: cover;
      flex: 0 0 36px;
      border-radius: var(--ds-radius-sm);
    }
    .anomaly-thumb--missing {
      display: grid;
      place-items: center;
      background: var(--aa-surface-subtle);
    }
    .priority-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: var(--aa-grid-font-secondary);
      line-height: 1.3;
      font-weight: 600;
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      padding: 3px 8px;
      user-select: none;
      white-space: nowrap;
    }
    .priority-pill--level-0 {
      background: rgba(33, 95, 155, 0.09);
      border-color: rgba(33, 95, 155, 0.3);
      color: #1e5a8f;
    }
    .priority-pill--level-1 {
      background: rgba(217, 119, 6, 0.1);
      border-color: rgba(217, 119, 6, 0.32);
      color: #b45309;
    }
    .priority-pill--level-2 {
      background: rgba(234, 88, 12, 0.1);
      border-color: rgba(234, 88, 12, 0.32);
      color: #c2410c;
    }
    .priority-pill--level-3,
    .priority-pill--level-4 {
      background: rgba(183, 28, 28, 0.09);
      border-color: rgba(183, 28, 28, 0.32);
      color: #b71c1c;
    }
    .priority-pill--none {
      background: var(--aa-surface-subtle);
      border-color: var(--aa-border-subtle);
      color: var(--aa-text-muted);
    }
    .location-sub {
      text-transform: capitalize;
    }
    .defect-chips {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-top: 2px;
      max-width: 100%;
      vertical-align: middle;
    }
    .defect-chip {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      height: 18px !important;
      padding: 0 5px !important;
      font-size: var(--aa-font-micro, 11px) !important;
      font-weight: 600 !important;
      line-height: 1 !important;
      letter-spacing: 0.02em !important;
      border-radius: var(--ds-radius-sm, 4px) !important;
      border: 1px solid transparent !important;
      user-select: none;
      flex: 0 0 auto;
      margin: 0 !important;
      vertical-align: middle;

      .v-chip__content {
        padding: 0 !important;
        line-height: 1 !important;
        display: inline-flex;
        align-items: center;
      }
    }
    .defect-chip--cat-a {
      background: rgba(3, 98, 76, 0.09) !important;
      border-color: rgba(3, 98, 76, 0.3) !important;
      color: #03624c !important;
    }
    .defect-chip--cat-b {
      background: rgba(217, 119, 6, 0.1) !important;
      border-color: rgba(217, 119, 6, 0.32) !important;
      color: #b45309 !important;
    }
    .defect-chip--cat-c {
      background: rgba(234, 88, 12, 0.1) !important;
      border-color: rgba(234, 88, 12, 0.32) !important;
      color: #c2410c !important;
    }
    .defect-chip--cat-d {
      background: rgba(183, 28, 28, 0.09) !important;
      border-color: rgba(183, 28, 28, 0.32) !important;
      color: #b71c1c !important;
    }
    .defect-chip--ri-0,
    .defect-chip--ri-1 {
      background: rgba(33, 95, 155, 0.08) !important;
      border-color: rgba(33, 95, 155, 0.28) !important;
      color: #1e5a8f !important;
    }
    .defect-chip--ri-2,
    .defect-chip--ri-3 {
      background: rgba(217, 119, 6, 0.09) !important;
      border-color: rgba(217, 119, 6, 0.3) !important;
      color: #b45309 !important;
    }
    .defect-chip--ri-4,
    .defect-chip--ri-5 {
      background: rgba(183, 28, 28, 0.09) !important;
      border-color: rgba(183, 28, 28, 0.3) !important;
      color: #b71c1c !important;
    }
    .defect-chip--neutral,
    .defect-chip--cat-other,
    .defect-chip--ri-custom {
      background: var(--aa-surface-subtle) !important;
      border-color: var(--aa-border-default) !important;
      color: var(--aa-text-secondary) !important;
    }

    &[data-density='compact'] {
      .defect-chips {
        margin-top: 1px;
      }
      .defect-chip {
        height: 16px !important;
        font-size: 10px !important;
        padding: 0 4px !important;
      }
    }
    .pagination {
      justify-content: flex-end;
    }
    .pagination__settings {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--ds-space-md);
      margin-right: auto;
    }
    .pagination__density {
      display: flex;
      align-items: center;
      gap: var(--ds-space-sm);
      white-space: nowrap;
    }
    .pagination select {
      border: 1px solid var(--aa-border-default);
      border-radius: var(--ds-radius-sm);
      color: var(--aa-text-primary);
      background: var(--aa-surface);
      padding: 4px 8px;
      margin-left: 8px;
      appearance: auto;
    }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
    }
    @media (max-width: 1000px) {
      .verified-body {
        flex-direction: column;
      }
    }
    @media (max-width: 500px) {
      .verified-heading {
        flex-wrap: wrap;
      }
      col.c-id {
        width: 150px;
      }
    }
  }
  .theme--dark .verified-anomalies-table,
  .theme-dark .verified-anomalies-table {
    @include ai-analysis-tokens-dark;

    .defect-chip--cat-a {
      background: rgba(44, 194, 149, 0.14) !important;
      border-color: rgba(44, 194, 149, 0.35) !important;
      color: #2cc295 !important;
    }
    .defect-chip--cat-b {
      background: rgba(245, 158, 11, 0.14) !important;
      border-color: rgba(245, 158, 11, 0.35) !important;
      color: #fbbf24 !important;
    }
    .defect-chip--cat-c {
      background: rgba(249, 115, 22, 0.14) !important;
      border-color: rgba(249, 115, 22, 0.35) !important;
      color: #fb923c !important;
    }
    .defect-chip--cat-d {
      background: rgba(239, 68, 68, 0.14) !important;
      border-color: rgba(239, 68, 68, 0.35) !important;
      color: #f87171 !important;
    }
    .defect-chip--ri-0,
    .defect-chip--ri-1 {
      background: rgba(96, 165, 250, 0.14) !important;
      border-color: rgba(96, 165, 250, 0.35) !important;
      color: #93c5fd !important;
    }
    .defect-chip--ri-2,
    .defect-chip--ri-3 {
      background: rgba(245, 158, 11, 0.14) !important;
      border-color: rgba(245, 158, 11, 0.35) !important;
      color: #fbbf24 !important;
    }
    .defect-chip--ri-4,
    .defect-chip--ri-5 {
      background: rgba(239, 68, 68, 0.14) !important;
      border-color: rgba(239, 68, 68, 0.35) !important;
      color: #f87171 !important;
    }
    .priority-pill--level-0 {
      background: rgba(96, 165, 250, 0.14) !important;
      border-color: rgba(96, 165, 250, 0.35) !important;
      color: #93c5fd !important;
    }
    .priority-pill--level-1 {
      background: rgba(245, 158, 11, 0.14) !important;
      border-color: rgba(245, 158, 11, 0.35) !important;
      color: #fbbf24 !important;
    }
    .priority-pill--level-2 {
      background: rgba(249, 115, 22, 0.14) !important;
      border-color: rgba(249, 115, 22, 0.35) !important;
      color: #fb923c !important;
    }
    .priority-pill--level-3,
    .priority-pill--level-4 {
      background: rgba(239, 68, 68, 0.14) !important;
      border-color: rgba(239, 68, 68, 0.35) !important;
      color: #f87171 !important;
    }
    .priority-pill--none {
      background: rgba(255, 255, 255, 0.06) !important;
      border-color: rgba(255, 255, 255, 0.18) !important;
      color: var(--aa-text-muted) !important;
    }
    .defect-chip--neutral,
    .defect-chip--cat-other,
    .defect-chip--ri-custom {
      background: rgba(255, 255, 255, 0.06) !important;
      border-color: rgba(255, 255, 255, 0.18) !important;
      color: var(--aa-text-primary) !important;
    }
  }
  .verified-anomaly-thumb-tooltip .anomaly-thumb-preview,
  .verified-anomaly-thumb-tooltip .anomaly-thumb-preview img {
    display: block;
    max-width: 280px;
    max-height: 220px;
  }
  .verified-anomaly-thumb-tooltip .anomaly-thumb-preview img {
    object-fit: contain;
  }
</style>
